import React, { useState, useEffect, useRef } from 'react';
import {
    Card, CardBody, Button, Chip, Spinner, Typography,
    Dialog, DialogHeader, DialogBody, DialogFooter,
} from '@material-tailwind/react';
import {
    FileText, Download, Eye, Plus, Filter, ChevronLeft, ChevronRight,
    Calendar, RefreshCw, Search, X, Send,
} from 'lucide-react';
import moment from 'moment';
import { useGetInvoices, useGenerateInvoices, useUpdateInvoiceStatus } from '../../custom-hooks/use-invoices';
import { INVOICE_STATUSES } from '../../utils/enums';
import { formatNumberWithCommas, Notify } from '../../utils/functions';
import { downloadInvoicePdf } from '../../utils/invoice-pdf';
import { GetInvoiceById, GetInvoiceSettings } from '../../services/backend';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../../custom-hooks/confirm-dialog';
import { usePermissions } from '../../custom-hooks/use-permissions';
import CreateInvoiceModal from './CreateInvoiceModal';

const statusColors = {
    draft: 'gray',
    delivered: 'blue',
    partially_paid: 'amber',
    paid: 'green',
    void: 'red',
};

const statusLabels = {
    draft: 'Draft',
    delivered: 'Delivered',
    partially_paid: 'Partially Paid',
    paid: 'Paid',
    void: 'Void',
};

const InvoicesPage = () => {
    const { can } = usePermissions();
    const canViewDetails = can('FX13-1-1', 'R');
    const navigate = useNavigate();
    const [filters, setFilters] = useState({ status: '', month: '', year: '', search: '', page: 1, limit: 20 });
    const [searchInput, setSearchInput] = useState('');
    const [generateDialog, setGenerateDialog] = useState(false);
    const [createInvoiceModal, setCreateInvoiceModal] = useState(false);
    const [generateMonth, setGenerateMonth] = useState(moment().month() + 1);
    const [generateYear, setGenerateYear] = useState(moment().year());
    const [selectedIds, setSelectedIds] = useState(new Set());
    const headerCheckboxRef = useRef(null);

    // Debounce: fire search after 1.5s of no typing
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
        }, 1500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Clear selection when filters/page change
    useEffect(() => {
        setSelectedIds(new Set());
    }, [filters]);

    const { data, isLoading, refetch } = useGetInvoices(filters);
    const generateMutation = useGenerateInvoices();
    const statusMutation = useUpdateInvoiceStatus();
    const { confirm, ConfirmationDialog } = useConfirm();

    const invoices = data?.invoices || [];
    const pagination = data?.pagination || { total: 0, page: 1, totalPages: 1 };

    const draftInvoices = invoices.filter(inv => inv.status === INVOICE_STATUSES.DRAFT);
    const allDraftSelected = draftInvoices.length > 0 && draftInvoices.every(inv => selectedIds.has(inv._id));
    const someSelected = selectedIds.size > 0;

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someSelected && !allDraftSelected;
        }
    }, [someSelected, allDraftSelected]);

    const toggleSelectAll = () => {
        if (allDraftSelected) setSelectedIds(new Set());
        else setSelectedIds(new Set(draftInvoices.map(inv => inv._id)));
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleBulkPublish = async () => {
        const ids = [...selectedIds];
        const isConfirmed = await confirm({
            title: 'Publish Invoices',
            message: `Publishing ${ids.length} invoice(s) will make them visible to customers on their portal. Proceed?`,
            confirmText: 'Publish All',
            cancelText: 'Cancel',
            confirmBtnColor: 'blue',
        });
        if (!isConfirmed) return;
        try {
            await Promise.all(ids.map(id =>
                statusMutation.mutateAsync({ invoiceId: id, data: { status: INVOICE_STATUSES.DELIVERED } })
            ));
            Notify(`${ids.length} invoice(s) published successfully`, 'success');
            setSelectedIds(new Set());
            refetch();
        } catch {
            Notify('Some invoices failed to publish', 'error');
        }
    };

    const handleGenerate = async () => {
        try {
            const res = await generateMutation.mutateAsync({ month: generateMonth, year: generateYear });
            Notify(res.data?.message || 'Invoices generated', 'success');
            setGenerateDialog(false);
            refetch();
        } catch (error) {
            Notify(error?.response?.data?.message || 'Generation failed', 'error');
        }
    };

    const handleStatusChange = async (invoiceId, newStatus) => {
        try {
            await statusMutation.mutateAsync({ invoiceId, data: { status: newStatus } });
            Notify(`Status updated to ${statusLabels[newStatus]}`, 'success');
            refetch();
        } catch (error) {
            Notify('Failed to update status', 'error');
        }
    };

    const handleDownloadPdf = async (invoiceId) => {
        try {
            const [invoiceRes, settingsRes] = await Promise.all([
                GetInvoiceById(invoiceId),
                GetInvoiceSettings().catch(() => null),
            ]);
            const invoice = invoiceRes.data?.invoice;
            if (invoice) {
                downloadInvoicePdf(invoice, settingsRes?.data?.settings || {});
            }
        } catch (error) {
            Notify('Failed to download PDF', 'error');
        }
    };

    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: moment().month(i).format('MMMM'),
    }));

    const currentYear = moment().year();
    const yearOptions = Array.from({ length: 4 }, (_, i) => currentYear - i);

    return (
        <div className="p-4 md:p-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div className="text-2xl font-thin">Invoices</div>
                <div className="flex flex-wrap gap-2">
                    {someSelected && (
                        <Button
                            variant="gradient"
                            color="blue"
                            className="flex items-center gap-2 rounded-full"
                            onClick={handleBulkPublish}
                            disabled={statusMutation.isLoading}
                            size="sm"
                        >
                            <Send className="h-4 w-4" />
                            Publish Selected ({selectedIds.size})
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="gray"
                        className="flex items-center gap-2 rounded-full"
                        onClick={() => setCreateInvoiceModal(true)}
                        size="sm"
                    >
                        <Plus className="h-4 w-4" />
                        Create Manual
                    </Button>
                    <Button
                        variant="gradient"
                        color="gray"
                        className="flex items-center gap-2 rounded-full"
                        onClick={() => setGenerateDialog(true)}
                        size="sm"
                    >
                        <Plus className="h-4 w-4" />
                        Generate
                    </Button>
                    <Button
                        variant="outlined"
                        color="gray"
                        className="flex items-center gap-2 rounded-full"
                        onClick={refetch}
                        size="sm"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6 items-center">
                {/* Search box */}
                <div className="relative flex-1 min-w-[200px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name or invoice #..."
                        className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                    {searchInput && (
                        <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => { setSearchInput(''); setFilters(prev => ({ ...prev, search: '', page: 1 })); }}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-2 text-gray-500">
                    <Filter className="h-4 w-4" />
                    <span className="text-sm">Filter:</span>
                </div>

                {/* Status filter */}
                <select
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                >
                    <option value="">All Statuses</option>
                    {Object.entries(statusLabels).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </select>

                {/* Month filter */}
                <select
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filters.month}
                    onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value, page: 1 }))}
                >
                    <option value="">All Months</option>
                    {monthOptions.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                </select>

                {/* Year filter */}
                <select
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filters.year}
                    onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value, page: 1 }))}
                >
                    <option value="">All Years</option>
                    {yearOptions.map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="flex justify-center items-center min-h-[30vh]">
                    <Spinner />
                </div>
            ) : invoices.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-lg">
                    <FileText className="h-12 w-12 text-gray-400 mb-4" />
                    <Typography variant="h6" color="gray">No invoices found</Typography>
                    <Typography variant="small" className="text-gray-500 mt-2 text-center max-w-md">
                        Use the Generate button to create invoices for a billing period.
                    </Typography>
                </div>
            ) : (
                <>
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full min-w-[800px] text-left">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="py-3 px-2 w-10">
                                        <input
                                            ref={headerCheckboxRef}
                                            type="checkbox"
                                            className="h-4 w-4 cursor-pointer accent-blue-600"
                                            checked={allDraftSelected}
                                            onChange={toggleSelectAll}
                                            disabled={draftInvoices.length === 0}
                                            title="Select all draft invoices"
                                        />
                                    </th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Invoice #</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Customer</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Period</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase text-right">Total</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase text-right">Paid</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase text-right">Balance</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Due Date</th>
                                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr
                                        key={inv._id}
                                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${canViewDetails ? 'cursor-pointer' : ''}`}
                                        onClick={() => canViewDetails && navigate(`/invoices/${inv._id}`)}
                                    >
                                        <td className="py-3 px-2 w-10" onClick={(e) => e.stopPropagation()}>
                                            {inv.status === INVOICE_STATUSES.DRAFT && (
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 cursor-pointer accent-blue-600"
                                                    checked={selectedIds.has(inv._id)}
                                                    onChange={() => toggleSelect(inv._id)}
                                                />
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-sm font-medium text-blue-600 hover:underline">{inv.invoiceNumber}</td>
                                        <td className="py-3 px-4 text-sm">
                                            <div 
                                                className="cursor-pointer hover:underline hover:text-blue-600 transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    inv.customer && navigate(`/customer/${inv.customer._id}`);
                                                }}
                                            >
                                                {inv.customer?.name || 'N/A'}
                                            </div>
                                            <div className="text-xs text-gray-500">{inv.customer?.email}</div>
                                        </td>
                                        <td className="py-3 px-4 text-sm">
                                            {moment({ month: inv.billingPeriod?.month - 1, year: inv.billingPeriod?.year }).format('MMM YYYY')}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right font-medium">
                                            PKR {formatNumberWithCommas(inv.totalAmount)}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right text-green-600">
                                            PKR {formatNumberWithCommas(inv.paidAmount)}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right text-red-600">
                                            PKR {formatNumberWithCommas(inv.balanceAmount)}
                                        </td>
                                        <td className="py-3 px-4">
                                            <Chip
                                                value={statusLabels[inv.status] || inv.status}
                                                color={statusColors[inv.status] || 'gray'}
                                                size="sm"
                                                variant="ghost"
                                                className="text-[10px] uppercase font-bold rounded-full text-center"
                                            />
                                            {/* Pending customer payments badge */}
                                            {inv.payments?.some(p => p.markedByCustomer && !p.approvedByAdmin) && (
                                                <Chip value="Pending Approval" color="amber" size="sm" className="text-[10px] uppercase font-bold rounded-full mt-1 text-center" variant="outlined" />
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-sm">
                                            {inv.dueDate ? moment(inv.dueDate).format('MMM DD') : '-'}
                                        </td>
                                        <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1">
                                                {inv.status === INVOICE_STATUSES.DRAFT && (
                                                    <button
                                                        className="text-[10px] px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors font-bold uppercase tracking-wider whitespace-nowrap"
                                                        onClick={async () => {
                                                            const isConfirmed = await confirm({
                                                                title: 'Publish Invoice',
                                                                message: 'Publishing this invoice will make it visible to the customer on their portal. Proceed?',
                                                                confirmText: 'Publish',
                                                                cancelText: 'Cancel',
                                                                confirmBtnColor: 'blue',
                                                            });
                                                            if (isConfirmed) {
                                                                handleStatusChange(inv._id, INVOICE_STATUSES.DELIVERED);
                                                            }
                                                        }}
                                                        title="Make visible to Customer"
                                                    >
                                                        Publish
                                                    </button>
                                                )}
                                                {canViewDetails && (
                                                    <button
                                                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                                        onClick={() => navigate(`/invoices/${inv._id}`)}
                                                        title="View Details"
                                                    >
                                                        <Eye className="h-4 w-4 text-gray-600" />
                                                    </button>
                                                )}
                                                <button
                                                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                                    onClick={() => handleDownloadPdf(inv._id)}
                                                    title="Download PDF"
                                                >
                                                    <Download className="h-4 w-4 text-gray-600" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                        {invoices.map((inv) => (
                            <Card key={inv._id} className="shadow-none border border-gray-200">
                                <CardBody className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-start gap-2">
                                            {inv.status === INVOICE_STATUSES.DRAFT && (
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 mt-0.5 cursor-pointer accent-blue-600 flex-shrink-0"
                                                    checked={selectedIds.has(inv._id)}
                                                    onChange={() => toggleSelect(inv._id)}
                                                />
                                            )}
                                            <div>
                                                <div className="text-sm font-medium">{inv.invoiceNumber}</div>
                                                <div
                                                    className="text-xs text-gray-500 cursor-pointer hover:underline hover:text-blue-600"
                                                    onClick={() => inv.customer && navigate(`/customer/${inv.customer._id}`)}
                                                >
                                                    {inv.customer?.name}
                                                </div>
                                            </div>
                                        </div>
                                        <Chip
                                            value={statusLabels[inv.status] || inv.status}
                                            color={statusColors[inv.status] || 'gray'}
                                            size="sm"
                                            variant="ghost"
                                            className="text-[10px] uppercase font-bold rounded-full"
                                        />
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                                        <div>
                                            <div className="text-xs text-gray-500">Total</div>
                                            <div className="text-sm font-medium">{formatNumberWithCommas(inv.totalAmount)}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">Paid</div>
                                            <div className="text-sm font-medium text-green-600">{formatNumberWithCommas(inv.paidAmount)}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500">Balance</div>
                                            <div className="text-sm font-medium text-red-600">{formatNumberWithCommas(inv.balanceAmount)}</div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {canViewDetails && (
                                            <Button size="sm" variant="outlined" color="gray" className="flex-1 flex items-center justify-center gap-1" onClick={() => navigate(`/invoices/${inv._id}`)}>
                                                <Eye className="h-3 w-3" /> View
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outlined" color="gray" className="flex-1 flex items-center justify-center gap-1" onClick={() => handleDownloadPdf(inv._id)}>
                                            <Download className="h-3 w-3" /> PDF
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-6">
                        <Typography variant="small" className="text-gray-500">
                            Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                        </Typography>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Typography variant="small" className="text-gray-500">Show</Typography>
                                <select
                                    className="border border-gray-300 rounded-md text-sm px-2 py-1 text-gray-700"
                                    value={filters.limit}
                                    onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                                >
                                    {[20, 50, 100, 200].map(n => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                            </div>
                            {pagination.totalPages > 1 && (
                                <div className="flex gap-2">
                                    <Button
                                        size="sm" variant="outlined" color="gray"
                                        disabled={pagination.page <= 1}
                                        onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        size="sm" variant="outlined" color="gray"
                                        disabled={pagination.page >= pagination.totalPages}
                                        onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Generate Dialog */}
            <Dialog open={generateDialog} handler={() => setGenerateDialog(false)} size="xs">
                <DialogHeader>Generate Invoices</DialogHeader>
                <DialogBody className="space-y-4">
                    <Typography variant="small" className="text-gray-600">
                        Generate invoices for all customers with active bookings for the selected month.
                    </Typography>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">Month</label>
                            <select
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={generateMonth}
                                onChange={(e) => setGenerateMonth(parseInt(e.target.value))}
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                        {moment().month(i).format('MMMM')}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm text-gray-600 mb-1 block">Year</label>
                            <input
                                type="number"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                value={generateYear}
                                onChange={(e) => setGenerateYear(parseInt(e.target.value))}
                                min={2024}
                                max={2030}
                            />
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" onClick={() => setGenerateDialog(false)} className="mr-2">
                        Cancel
                    </Button>
                    <Button
                        variant="gradient"
                        color="gray"
                        onClick={handleGenerate}
                        disabled={generateMutation.isLoading}
                        className="flex items-center gap-2"
                    >
                        {generateMutation.isLoading ? <Spinner className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
                        Generate
                    </Button>
                </DialogFooter>
            </Dialog>
            <ConfirmationDialog />

            <CreateInvoiceModal
                isOpen={createInvoiceModal}
                onClose={() => setCreateInvoiceModal(false)}
                onSuccess={() => refetch()}
            />
        </div>
    );
};

export default InvoicesPage;
